// Hook for accessing theme-aware colors in components
// Provides mode-aware color values that automatically update with theme changes

import { useTheme } from '@mui/material/styles';
import {
  navyBlue,
  getPriorityColors,
  getStatusColors,
  getRoleColors,
  getOverdueColors,
  getBackgroundColors,
  getIssueTypeColors,
} from '../constants/colors';

// Brighter blue for dark mode readability
const brightBlue = '#64b5f6';

export function useThemeColors() {
  const theme = useTheme();
  const mode = theme.palette.mode;

  // Mode-aware accent color: navy in light mode, bright blue in dark mode
  const accentBlue = mode === 'dark' ? brightBlue : navyBlue;

  return {
    // Brand color (consistent across modes - use for backgrounds/gradients)
    navyBlue,

    // Mode-aware accent color (use for text/icons that need to be readable)
    accentBlue,
    brightBlue,

    // Semantic colors (mode-aware)
    priorityColors: getPriorityColors(mode),
    statusColors: getStatusColors(mode),
    roleColors: getRoleColors(mode),
    overdueColors: getOverdueColors(mode),
    issueTypeColors: getIssueTypeColors(mode),

    // Background colors (mode-aware)
    ...getBackgroundColors(mode),

    // Current mode
    mode,
    isDark: mode === 'dark',

    // Gradients - brighter in dark mode for better visibility
    gradientPrimary: mode === 'dark'
      ? `linear-gradient(135deg, #3d6a99 0%, #2c5282 100%)`
      : `linear-gradient(135deg, ${navyBlue} 0%, #2c4a6e 100%)`,
    gradientPrimaryHover: mode === 'dark'
      ? `linear-gradient(135deg, #4a7ab0 0%, #3d6a99 100%)`
      : `linear-gradient(135deg, #2c4a6e 0%, ${navyBlue} 100%)`,
  };
}

export default useThemeColors;
